import { useRef, useState } from 'react'
import { useSearch } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import { CheckCircle2, Download, FileSpreadsheet, Upload as UploadIcon, XCircle } from 'lucide-react'

import { useMe } from '@/features/auth/api'
import { Link } from '@/lib/router'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Chip, ChipGroup } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'

import { dateTime, num, relativeTime } from '@/lib/format'
import { cn } from '@/lib/utils'

import {
  templateDownloadUrl,
  type AllocationSlot,
  type IngestKind,
  type UploadResponse,
  useBatches,
  useTemplates,
  useUploadBatch,
} from './api'

const KINDS: { value: IngestKind; label: string; help: string }[] = [
  { value: 'COLLECTION_LIST', label: 'Collection list',  help: 'Loan Account No — this month’s loans to collect; customers, loans and dues come from the LMS' },
  { value: 'CUSTOMER_MASTER', label: 'Customer master',  help: 'Customer Ref · Full Name · Mobile · address · references · guarantor · spouse · profession' },
  { value: 'LOAN_MASTER',     label: 'Loan master',      help: 'Loan Account No · Customer Ref · Product · Sanction · Disbursal · EMI · tenure · co-lending' },
  { value: 'DPD_DUES',        label: 'Daily DPD dues',   help: 'Loan No · As Of · DPD · Bucket · POS · overdue principal + interest · charges (BCC / LPP / penalties / fees)' },
  { value: 'PRIORITY_DELTA',  label: 'Priority override', help: 'Loan No · Priority (P1/P2/P3/P4 or High/Medium/Low) — manager override that beats the DPD recompute until the next upload' },
  { value: 'DNC_DELTA',       label: 'Do-not-call flag',  help: 'Loan No · DNC Yes/No — flips the linked customer’s DNC flag; ops blocks contact attempts' },
  { value: 'ALLOCATION_DELTA', label: 'Bulk allocation / unallocation', help: 'Loan Account Number · Agent Name (or Emp ID) · Allocation Type (FIELD / TELE, optional) · Action (ALLOCATE / UNALLOCATE, blank = ALLOCATE) — ALLOCATE gives the loan to the agent, moving it from whoever holds it; UNALLOCATE removes it (agent optional, must be the current holder)' },
]

// On the Graviton tier customers, loans and dues come from the LMS, so the
// monthly collection list replaces the three master/dues uploads.
const LMS_FED: IngestKind[] = ['CUSTOMER_MASTER', 'LOAN_MASTER', 'DPD_DUES']

export function IngestionPage() {
  const { data: me } = useMe()
  const graviton = me?.tenantTier === 'GRAVITON'
  const kinds = KINDS.filter((k) =>
    graviton ? !LMS_FED.includes(k.value) : k.value !== 'COLLECTION_LIST',
  )
  // ?kind= preselects a kind — the Allocations screen links straight to
  // bulk loan allocation.
  const search = useSearch({ strict: false }) as { kind?: string }
  const [picked, setPicked] = useState<IngestKind | null>(
    kinds.some((k) => k.value === search.kind) ? (search.kind as IngestKind) : null,
  )
  const kind = kinds.find((k) => k.value === picked)?.value ?? kinds[0]!.value
  // Bulk loan allocation: the slot for rows whose Allocation Type is blank.
  const [slot, setSlot] = useState<AllocationSlot>('FIELD')
  const uploadSlot = kind === 'ALLOCATION_DELTA' ? { slot } : {}
  const [file, setFile] = useState<File | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const upload = useUploadBatch()
  const { data: batches, isPending } = useBatches()
  const { data: templates } = useTemplates()
  const activeTemplate = templates?.find((t) => t.kind === kind)

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!file) return
    upload.mutate({ kind, file, ...uploadSlot }, {
      onSuccess: (data: UploadResponse) => {
        // On DUPLICATE nothing was processed, and the operator may want to
        // force it through — so hold on to the file for the retry button.
        if (data.status === 'DUPLICATE') return
        setFile(null)
        if (fileRef.current) fileRef.current.value = ''
      },
    })
  }

  function onForceReprocess() {
    if (!file) return
    upload.mutate({ kind, file, force: true, ...uploadSlot }, {
      onSuccess: () => {
        setFile(null)
        if (fileRef.current) fileRef.current.value = ''
      },
    })
  }

  const errorMessage = upload.isError
    ? isAxiosError(upload.error) && upload.error.response?.status === 403
      ? 'You do not have INGESTION_UPLOAD permission.'
      : isAxiosError(upload.error) && upload.error.response?.data?.message
      ? upload.error.response.data.message
      : 'Upload failed.'
    : null

  const active = kinds.find((k) => k.value === kind)!

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div>
        <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">Data pipeline</p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-navy-900">Ingestion</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Upload an Excel file. The pipeline parses, canonicalises, commits accepted rows
          and writes rejections to a per-batch report. The worker picks the file up within seconds.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>New upload</CardTitle>
              <CardDescription>{active.help}</CardDescription>
            </div>
            <Badge tone="navy">XLSX · ≤ 20 MB</Badge>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <ChipGroup>
              {kinds.map((k) => (
                <Chip key={k.value} active={kind === k.value} onClick={() => setPicked(k.value)}>
                  {k.label}
                </Chip>
              ))}
            </ChipGroup>
            <a
              href={templateDownloadUrl(kind)}
              download
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-navy-800 hover:border-navy-200 hover:bg-navy-50 active:translate-y-px transition-all duration-fluid ease-fluid motion-reduce:transition-none motion-reduce:active:translate-y-0"
            >
              <Download className="h-3.5 w-3.5" />
              Download {KINDS.find((k) => k.value === kind)?.label.toLowerCase() ?? 'template'} template
            </a>
          </div>

          {kind === 'ALLOCATION_DELTA' && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Label>Default slot</Label>
              <ChipGroup>
                <Chip active={slot === 'FIELD'} onClick={() => setSlot('FIELD')}>FIELD</Chip>
                <Chip active={slot === 'TELE'}  onClick={() => setSlot('TELE')}>TELE</Chip>
              </ChipGroup>
              <span className="text-xs text-muted-foreground">
                Used for rows whose <span className="font-mono">Allocation Type</span> is blank —{' '}
                {slot === 'FIELD' ? 'a collection agent’s field slot.' : 'a telecaller’s tele slot.'}{' '}
                A filled column wins.
              </span>
            </div>
          )}

          {activeTemplate && (
            <div className="mt-3 overflow-hidden rounded-md border border-slate-200 bg-navy-50/40">
              <table className="w-full text-xs">
                <thead className="bg-navy-100/60 text-left text-2xs uppercase tracking-wider text-navy-700">
                  <tr>
                    <th className="px-3 py-1.5 font-medium">Column</th>
                    <th className="px-3 py-1.5 font-medium">Required</th>
                    <th className="px-3 py-1.5 font-medium">Help</th>
                  </tr>
                </thead>
                <tbody>
                  {activeTemplate.columns.map((c) => (
                    <tr key={c.name} className="border-t border-slate-200 last:border-0">
                      <td className="px-3 py-1.5 font-mono text-navy-900">{c.name}</td>
                      <td className="px-3 py-1.5">
                        {c.required
                          ? <Badge tone="navy">Required</Badge>
                          : <span className="text-muted-foreground">Optional</span>}
                      </td>
                      <td className="px-3 py-1.5 text-navy-800/90">{c.help}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {errorMessage && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>Upload failed</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
          {upload.isSuccess && upload.data && (
            <Alert variant={upload.data.status === 'DUPLICATE' ? 'warning' : 'positive'} className="mb-4">
              <AlertTitle>
                {upload.data.status === 'DUPLICATE' ? 'Already uploaded' : 'Accepted'} — batch{' '}
                <Link
                  to="/ingest/$batchId"
                  params={{ batchId: upload.data.batchId }}
                  className="font-mono text-xs underline-offset-4 hover:underline"
                >
                  {upload.data.batchId.slice(0, 8)}…
                </Link>
              </AlertTitle>
              <AlertDescription>
                {upload.data.status === 'DUPLICATE' ? (
                  <div className="space-y-2">
                    <p>
                      {upload.data.message ??
                        'This exact file was already uploaded for this kind and was NOT re-processed.'}
                    </p>
                    {file && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={upload.isPending}
                        onClick={onForceReprocess}
                      >
                        Re-process anyway
                      </Button>
                    )}
                  </div>
                ) : (
                  'The pipeline is processing it now. It will appear in the list below.'
                )}
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={onSubmit} className="grid gap-3 sm:grid-cols-[1fr_auto] items-end">
            <div className="space-y-1.5">
              <Label htmlFor="file">File</Label>
              <Input
                id="file"
                ref={fileRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                required
                className="cursor-pointer"
              />
            </div>
            <Button type="submit" disabled={!file || upload.isPending} className="justify-self-end">
              <UploadIcon className="h-3.5 w-3.5" />
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </form>
        </CardContent>
        <CardFooter>
          <span className="text-2xs uppercase tracking-wider text-muted-foreground">
            SHA-256 idempotent · partial-commit on row failures
          </span>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Recent batches</CardTitle>
              <CardDescription>Auto-refreshes while anything is queued or running.</CardDescription>
            </div>
            <Badge tone="navy">{num(batches?.length ?? 0)} batches</Badge>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {isPending && <SkeletonRows rows={4} cols={6} />}
          {batches && batches.length === 0 && !isPending && (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-medium text-navy-900">No batches yet</p>
              <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                Drop the first file above. The pipeline will parse and commit accepted rows; rejections
                get a JSON report you can audit per row.
              </p>
            </div>
          )}
          {batches && batches.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
                  <tr>
                    <th className="px-5 py-2 font-medium">File</th>
                    <th className="px-3 py-2 font-medium">Kind</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 text-right font-medium">Accepted</th>
                    <th className="px-3 py-2 text-right font-medium">Rejected</th>
                    <th className="px-5 py-2 font-medium">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.batchId} className="border-b border-slate-200 last:border-0 transition-colors duration-fluid ease-fluid hover:bg-navy-50">
                      <td className="px-5 py-2">
                        <Link
                          to="/ingest/$batchId"
                          params={{ batchId: b.batchId }}
                          className="flex items-center gap-2 text-navy-900 underline-offset-4 hover:underline"
                        >
                          <FileSpreadsheet className="h-3.5 w-3.5 text-navy-500" />
                          <span className="truncate max-w-[18rem]">{b.fileName}</span>
                        </Link>
                        {/* A bare FAILED badge tells ops nothing actionable. The
                            first row error is almost always the mistake repeated
                            down the whole file, so it belongs here rather than
                            one click away on the detail screen. */}
                        {b.topRejectionReason && (
                          <p
                            className="mt-0.5 line-clamp-2 max-w-[26rem] break-words text-2xs leading-snug text-negative"
                            title={b.topRejectionReason}
                          >
                            {b.topRejectionReason}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-2xs uppercase tracking-wider text-muted-foreground">
                        {b.kind.replace(/_/g, ' ')}
                        {b.options?.slot && <span className="text-navy-700"> · {b.options.slot}</span>}
                      </td>
                      <td className="px-3 py-2"><StatusBadge status={b.status} /></td>
                      <td className="px-3 py-2 text-right tabular-nums text-navy-900">{num(b.acceptedCount)}</td>
                      <td className={cn('px-3 py-2 text-right tabular-nums', b.rejectedCount > 0 ? 'text-negative' : 'text-navy-900')}>
                        {num(b.rejectedCount)}
                      </td>
                      <td className="px-5 py-2 text-muted-foreground">
                        <span title={b.submittedAt ? dateTime(b.submittedAt) : undefined}>
                          {b.submittedAt ? relativeTime(b.submittedAt) : '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'DONE')           return <Badge tone="positive"><CheckCircle2 className="h-3 w-3" /> DONE</Badge>
  if (status === 'PARTIALLY_DONE') return <Badge tone="warning"><CheckCircle2 className="h-3 w-3" /> PARTIAL</Badge>
  if (status === 'FAILED')         return <Badge tone="negative"><XCircle className="h-3 w-3" /> FAILED</Badge>
  if (status === 'RUNNING')        return <Badge tone="info"><LiveDot /> RUNNING</Badge>
  if (status === 'QUEUED')         return <Badge tone="navy"><LiveDot /> QUEUED</Badge>
  return <Badge tone="slate">{status}</Badge>
}

/** A pulsing dot — used in place of spinners (we never spin on data). */
function LiveDot() {
  return (
    <span aria-hidden className="relative inline-flex h-1.5 w-1.5">
      <span className="absolute inset-0 animate-ping rounded-full bg-current opacity-60 motion-reduce:hidden" />
      <span className="relative inline-block h-1.5 w-1.5 rounded-full bg-current" />
    </span>
  )
}
